import React, { useEffect, useState } from "react";

const API_KEY = "SXai-6sxnvPBZ4vuHddTvFz20Kd3VEk-r1hcERMcM_c";
const BASE = "https://pwg.clay.in/api/external";
const HEADERS = { "accept": "*/*", "X-API-Key": API_KEY, "Content-Type": "application/json" };

const safe = (val) => {
  if (val === null || val === undefined) return "—";
  if (typeof val === "object") return JSON.stringify(val);
  return String(val);
};

const formatDate = (d) => {
  if (!d) return "—";
  try {
    return new Date(d).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return "—";
  }
};

// ─── Reusable Components ──────────────────────────────────────────────────────

const Modal = ({ title, onClose, children }) => (
  <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
      <div className="flex justify-between items-center px-6 py-4 border-b">
        <h2 className="font-semibold text-gray-800">{title}</h2>
        <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-lg leading-none">✕</button>
      </div>
      <div className="px-6 py-4">{children}</div>
    </div>
  </div>
);

const Field = ({ label, name, value, onChange, required, readOnly }) => (
  <div className="mb-3">
    <label className="block text-xs font-medium text-gray-500 mb-1">
      {label}{required && " *"}
    </label>
    <input
      name={name}
      value={value ?? ""}
      onChange={onChange}
      required={required}
      readOnly={readOnly}
      className={`w-full border rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 ${readOnly ? "bg-gray-50 text-gray-500" : ""}`}
    />
  </div>
);

const StatusMsg = ({ status }) =>
  status ? (
    <p className={`text-sm mb-3 ${status.ok ? "text-green-600" : "text-red-500"}`}>
      {status.msg}
    </p>
  ) : null;

const ModalFooter = ({ onClose, onSubmit, loading, label, color = "bg-blue-600" }) => (
  <div className="flex gap-2 justify-end mt-4">
    <button onClick={onClose} className="px-4 py-2 rounded-lg border text-sm text-gray-600 hover:bg-gray-50">
      Cancel
    </button>
    <button
      onClick={onSubmit}
      disabled={loading}
      className={`px-4 py-2 rounded-lg text-white text-sm ${color} disabled:opacity-50 hover:opacity-90 transition`}
    >
      {loading ? "Processing…" : label}
    </button>
  </div>
);

// ─── API Helper ───────────────────────────────────────────────────────────────

const callApi = async (endpoint, body) => {
  const res = await fetch(`${BASE}/${endpoint}`, {
    method: "POST",
    headers: HEADERS,
    body: JSON.stringify({ request: body }),
  });
  const data = await res.json();
  if (data.response?.status === "success") return { ok: true, msg: "Operation successful!" };
  const errMsg =
    data.response?.errors?.firstError?.message ||
    data.response?.status ||
    "Request failed";
  return { ok: false, msg: errMsg };
};

const useApiCall = () => {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const submit = async (endpoint, body) => {
    setLoading(true);
    setStatus(null);
    try { setStatus(await callApi(endpoint, body)); }
    catch (e) { setStatus({ ok: false, msg: e.message }); }
    finally { setLoading(false); }
  };
  return { loading, status, submit };
};

// ─── Swap Modal ───────────────────────────────────────────────────────────────

const SwapModal = ({ item, onClose }) => {
  const [form, setForm] = useState({
    type: "SwapESN",
    mdn: item?.esn ?? "",
    oldEsn: item?.esn ?? "",
    newEsn: "",
  });
  const { loading, status, submit } = useApiCall();
  const ch = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <Modal title="Swap ESN" onClose={onClose}>
      <Field label="MDN"     name="mdn"    value={form.mdn}    onChange={ch} required />
      <Field label="Old ESN" name="oldEsn" value={form.oldEsn} onChange={ch} required />
      <Field label="New ESN" name="newEsn" value={form.newEsn} onChange={ch} required />
      <StatusMsg status={status} />
      <ModalFooter onClose={onClose} onSubmit={() => submit("SwapEsn", form)} loading={loading} label="Swap" />
    </Modal>
  );
};

// ─── Deactivate Modal ─────────────────────────────────────────────────────────

const DeactivateModal = ({ item, onClose }) => {
  const [form, setForm] = useState({
    type: "Disconnect",
    mdn: item?.esn ?? "",
    esn: item?.esn ?? "",
  });
  const { loading, status, submit } = useApiCall();
  const ch = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  return (
    <Modal title="Deactivate Line" onClose={onClose}>
      <p className="text-sm text-gray-500 mb-4">
        This will disconnect the line. Confirm the details below.
      </p>
      <Field label="MDN"  name="mdn"  value={form.mdn}  onChange={ch} required />
      <Field label="ESN"  name="esn"  value={form.esn}  onChange={ch} required />
      <Field label="Type" name="type" value={form.type} onChange={ch} />
      <StatusMsg status={status} />
      <ModalFooter
        onClose={onClose}
        onSubmit={() => submit("Disconnect", form)}
        loading={loading}
        label="Deactivate"
        color="bg-red-600"
      />
    </Modal>
  );
};

// ─── Reconnect Modal ──────────────────────────────────────────────────────────

const ADDR_INIT = { street1: "", street2: "", city: "", state: "", zip: "" };

const ReconnectModal = ({ item, onClose }) => {
  const [form, setForm] = useState({
    type: "Reconnect",
    mdn: item?.mdn ?? "",
    esn: item?.esn ?? "",
    imei: "",
    plan: item?.planId ?? "",
    zip: "",
    billingCode: item?.billingCode ?? "",
    stateCode: "",
     e911Address: {
          street1: "123 Main St",
          street2: "JPEX Testing",
          city: "New York",
          state: "NY",
          zip: "942105",
        },
  });
  const { loading, status, submit } = useApiCall();
  const ch = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));
  

  return (
    <Modal title="Reconnect Line" onClose={onClose}>
      <div className="max-h-90 overflow-y-auto space-y-0 pr-1">
        <div className="grid grid-cols-2 gap-x-3">
          <Field label="MDN"          name="mdn"         value={form.mdn}         onChange={ch} required />
          <Field label="ESN"          name="esn"         value={form.esn}         onChange={ch} required />
          <Field label="IMEI/Phone Number"         name="imei"        value={form.imei}        onChange={ch} />
          <Field label="Plan"         name="plan"        value={form.plan}        onChange={ch} />
          <Field label="ZIP"          name="zip"         value={form.zip}         onChange={ch} />
          <Field label="Billing Code" name="billingCode" value={form.billingCode} onChange={ch} />
          <Field label="State Code"   name="stateCode"   value={form.stateCode}   onChange={ch} />
          <Field label="Type"         name="type"        value={form.type}        onChange={ch} />
        </div>
      
      </div>
      <StatusMsg status={status} />
      <ModalFooter
        onClose={onClose}
        onSubmit={() => submit("Reconnect", form)}
        loading={loading}
        label="Reconnect"
        color="bg-green-600"
      />
    </Modal>
  );
};

// ─── Main Table ───────────────────────────────────────────────────────────────

const MODAL_MAP = { swap: SwapModal, deactivate: DeactivateModal, reconnect: ReconnectModal };

const ActionBtn = ({ label, type, item, color, setModal }) => (
  <button
    onClick={() => setModal({ type, item })}
    className={`rounded-full px-3 py-1 text-xs font-medium text-white ${color} hover:opacity-80 transition`}
  >
    {label}
  </button>
);

const Uspwgpurchase = () => {
  const [data, setData]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState(null);
  const [modal, setModal]   = useState(null); // { type, item }

  useEffect(() => {
    fetch(`${BASE}/GetActivatedMDNs`, {
      headers: { "accept": "*/*", "X-API-Key": API_KEY },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.success) {
          setData(Array.isArray(d.data) ? d.data : []);
        } else {
          setError("API returned failure");
        }
      })
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <p className="p-6 text-gray-500">Loading…</p>;
  if (error)   return <p className="p-6 text-red-500">Error: {error}</p>;

  const ActiveModal = modal && MODAL_MAP[modal.type];

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b">
            {["Activated By", "Activated Date", "ESN", "Billing Code", "Plan ID", "Actions"].map((h) => (
              <th
                key={h}
                className="py-3 px-4 text-center text-xs font-semibold text-gray-500 uppercase tracking-wide"
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.map((item, i) => (
            <tr key={i} className="border-b hover:bg-gray-50 transition">
              <td className="py-3 px-4 text-center">{safe(item?.activatedBy)}</td>
              <td className="py-3 px-4 text-center text-gray-500">{formatDate(item?.activatedDate)}</td>
              <td className="py-3 px-4 text-center font-mono text-xs">{safe(item?.esn)}</td>
              <td className="py-3 px-4 text-center">{safe(item?.billingCode)}</td>
              <td className="py-3 px-4 text-center">{safe(item?.planId)}</td>
              <td className="py-3 px-4">
                <div className="flex gap-2 justify-center">
                  <ActionBtn label="Swap"       type="swap"       item={item} color="bg-blue-500"  setModal={setModal} />
                  <ActionBtn label="Deactivate" type="deactivate" item={item} color="bg-red-500"   setModal={setModal} />
                  <ActionBtn label="Reconnect" type="reconnect" item={item} color="bg-green-500" setModal={setModal} />
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {ActiveModal && <ActiveModal item={modal.item} onClose={() => setModal(null)} />}
    </div>
  );
};

export default Uspwgpurchase;